import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCurrentUserId } from '../utils/userUtils';

// List of all spreadsheet types to prefetch
const SPREADSHEET_TYPES = [
  'Client List',
  'Plan Touchpoints',
  'Requirements Checklist',
  'Plan Design and Elections',
  'Plan Performance',
  'Fund Performance',
  'Advisor Service Schedule',
  'Fee Schedule',
  'Participants Table',
  'Prospecting'
];

// Dashboard-related endpoints to prefetch
const DASHBOARD_ENDPOINTS = [
  { key: 'dashboard', path: '/dashboard' },
  { key: 'timeData', path: '/get_time_data' },
  { key: 'clientList', path: '/clients' },
  { key: 'userYears', path: '/user_years', noYear: true }
];

export const useSpreadsheetPrefetch = (year) => {
  const queryClient = useQueryClient();
  const userId = getCurrentUserId();
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [prefetchProgress, setPrefetchProgress] = useState(0);

  useEffect(() => {
    if (!userId) return;

    // Prefetch all spreadsheets and dashboard data in the background
    const prefetchAll = async () => {
      setIsPrefetching(true);
      setPrefetchProgress(0);
      
      let completed = 0;
      const totalSpreadsheets = SPREADSHEET_TYPES.length;
      const totalDashboard = DASHBOARD_ENDPOINTS.filter(e => !e.noYear || year).length;
      const total = totalSpreadsheets + totalDashboard;
      
      // Prefetch spreadsheets
      const spreadsheetPromises = SPREADSHEET_TYPES.map(async (spreadsheetType) => {
        try {
          await queryClient.prefetchQuery({
            queryKey: ['spreadsheets', spreadsheetType, year, userId],
            queryFn: async ({ queryKey }) => {
              const [, type, year, userId] = queryKey;
              const response = await fetch(
                `https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/${type}/${year}`
              );
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              const result = await response.json();
              return result;
            },
            staleTime: 30 * 60 * 1000, // 30 minutes
            cacheTime: 60 * 60 * 1000, // 60 minutes
          });
          
          completed++;
          setPrefetchProgress((completed / total) * 100);
          console.log(`Prefetched ${spreadsheetType} (${completed}/${total})`);
        } catch (error) {
          console.log(`Failed to prefetch ${spreadsheetType}:`, error);
          completed++;
          setPrefetchProgress((completed / total) * 100);
        }
      });

      // Prefetch dashboard data
      const dashboardPromises = DASHBOARD_ENDPOINTS.map(async (endpoint) => {
        // Skip year-dependent endpoints if no year provided
        if (endpoint.noYear && !year) return;
        
        try {
          let url;
          switch (endpoint.key) {
            case 'dashboard':
              url = `https://plansync-backend-234288438538.us-central1.run.app/api/dashboard/${userId}?year=${year}`;
              break;
            case 'timeData':
              url = `https://plansync-backend-234288438538.us-central1.run.app/api/get_time_data/${year}?user_id=${userId}`;
              break;
            case 'clientList':
              url = `https://plansync-backend-234288438538.us-central1.run.app/api/clients/${userId}/${year}`;
              break;
            case 'userYears':
              url = `https://plansync-backend-234288438538.us-central1.run.app/api/user_years/${userId}`;
              break;
            default:
              return;
          }
          
          await queryClient.prefetchQuery({
            queryKey: endpoint.noYear ? ['dashboard', endpoint.key, userId] : ['dashboard', endpoint.key, year, userId],
            queryFn: async () => {
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            },
            staleTime: 30 * 60 * 1000, // 30 minutes
            cacheTime: 60 * 60 * 1000, // 60 minutes
          });
          
          completed++;
          setPrefetchProgress((completed / total) * 100);
          console.log(`Prefetched ${endpoint.key} (${completed}/${total})`);
        } catch (error) {
          console.log(`Failed to prefetch ${endpoint.key}:`, error);
          completed++;
          setPrefetchProgress((completed / total) * 100);
        }
      });

      // Execute all prefetches
      await Promise.all([...spreadsheetPromises, ...dashboardPromises]);
      
      console.log('All data prefetched successfully');
      setIsPrefetching(false);
    };

    // Start prefetching after a short delay to not block initial render
    const timeoutId = setTimeout(prefetchAll, 1000);

    return () => clearTimeout(timeoutId);
  }, [queryClient, userId, year]);

  return { isPrefetching, prefetchProgress };
};